import React, { useState, useMemo, useEffect, useCallback, useDeferredValue } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  MenuItem,
  Tooltip,
  IconButton,
  Menu,
  Checkbox,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { Search as SearchIcon, ViewColumn as ViewColumnIcon, Close as CloseIcon } from '@mui/icons-material';
import { useCachedData } from '../hooks/useCachedData';
import { useQuery } from '@tanstack/react-query';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { api, endpoints } from '../api/config';

const labelForLtlColumn = (key) => {
  const labels = {
    poNumber: 'Customer PO#',
    poNumberId: 'Customer PO#',
    imageUrl: 'Carrier',
    palletSize: 'Pallet Size',
    shipdate: 'Ship Date',
    status: 'Status',
    palletCount: 'Pallet Count',
    arnValue: 'ARN Value',
  };
  return labels[key] || key;
};

const getStatusChipProps = (status) => {
  const statusLower = (status || '').toLowerCase();

  if (statusLower.includes('ready') || statusLower.includes('completed') || statusLower.includes('done')) {
    return { color: 'success', variant: 'filled' };
  }
  if (statusLower.includes('in progress') || statusLower.includes('picking') || statusLower.includes('processing')) {
    return { color: 'warning', variant: 'filled' };
  }
  if (statusLower.includes('pending') || statusLower.includes('scheduled') || statusLower.includes('waiting')) {
    return { color: 'info', variant: 'filled' };
  }
  if (statusLower.includes('error') || statusLower.includes('failed') || statusLower.includes('cancelled')) {
    return { color: 'error', variant: 'filled' };
  }
  if (statusLower.includes('none') || statusLower === '- none -' || !status) {
    return { color: 'default', variant: 'outlined' };
  }
  return { color: 'primary', variant: 'filled' };
};

const standardStatusChipSx = {
  fontWeight: 600,
  minWidth: 'fit-content',
  transition: 'none !important',
  animation: 'none !important',
  transform: 'none !important',
  '&:hover': {
    transform: 'none !important',
    transition: 'none !important',
  },
};

const tvStatusChipSx = {
  fontWeight: 600,
  minWidth: 'fit-content',
  fontSize: '1.1rem',
  height: 40,
  '& .MuiChip-label': { px: 2 },
  transition: 'none !important',
  animation: 'none !important',
  transform: 'none !important',
  '&:hover': {
    transform: 'none !important',
    transition: 'none !important',
  },
};

const LtlStatusCell = React.memo(({ displayStatus, status, isTvDisplay }) => {
  const finalStatus = displayStatus || status || 'No Status';
  const chipProps = getStatusChipProps(finalStatus);

  return (
    <Chip
      label={finalStatus}
      size={isTvDisplay ? 'medium' : 'small'}
      {...chipProps}
      sx={isTvDisplay ? tvStatusChipSx : standardStatusChipSx}
    />
  );
});

const carrierImageCache = new Map();

const CarrierLogoCell = React.memo(({ imageUrl, isTvDisplay, denseMode }) => {
  const fullImageUrl = imageUrl || '';
  const cachedStatus = carrierImageCache.get(fullImageUrl);
  const [loadState, setLoadState] = useState(cachedStatus || (fullImageUrl ? 'loading' : 'none'));

  const handleLoad = useCallback(() => {
    if (fullImageUrl) carrierImageCache.set(fullImageUrl, 'loaded');
    setLoadState('loaded');
  }, [fullImageUrl]);

  const handleError = useCallback(() => {
    if (fullImageUrl) carrierImageCache.set(fullImageUrl, 'failed');
    setLoadState('failed');
  }, [fullImageUrl]);

  if (!fullImageUrl || loadState === 'failed' || cachedStatus === 'failed') {
    return (
      <span
        style={{
          color: 'var(--mui-palette-text-primary, rgba(0, 0, 0, 0.87))',
          fontWeight: 700,
          fontSize: isTvDisplay ? '1.25rem' : '0.875rem',
          display: 'inline-block',
          lineHeight: 1,
        }}
      >
        TBD
      </span>
    );
  }

  const maxW = isTvDisplay ? 200 : 130;
  const maxH = isTvDisplay ? 52 : denseMode ? 15 : 24;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <img
        src={fullImageUrl}
        alt="Carrier"
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        style={{
          maxWidth: `${maxW}px`,
          maxHeight: `${maxH}px`,
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          objectPosition: 'center',
          display: 'block',
          flexShrink: 0,
          pointerEvents: 'none',
          transition: 'none',
          animation: 'none',
        }}
      />
    </div>
  );
});

const LtlTextCell = React.memo(({ value, fontSize, isTvDisplay }) => {
  const displayVal = value === null || value === undefined ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
  return (
    <span
      style={{
        fontSize: fontSize || (isTvDisplay ? '1.35rem' : '0.8125rem'),
        fontWeight: isTvDisplay ? 500 : 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'inline-block',
        lineHeight: 1.4,
      }}
    >
      {displayVal}
    </span>
  );
});

const labelForLineLevelColumn = (key) =>
  ({
    pallet_number: 'Pallet Number',
  }[key] ||
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' '));

const enrichLtlLineLevelRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const units = Array.isArray(row?.data) ? row.data : [];
    const palletNumbers = [
      ...new Set(
        units
          .map((unit) => String(unit?.pallet_number || '').trim())
          .filter((pallet) => pallet && pallet !== '0')
      ),
    ];
    return {
      ...row,
      pallet_number: palletNumbers.join('; '),
    };
  });

const LINE_LEVEL_COLUMN_WIDTHS = {
  pallet_number: 360,
  sscc_codes: 240,
  tracking_number: 200,
  picker_name: 180,
  picked_time: 180,
  ship_confirm_time: 180,
  movement: 160,
};

const LOCATION_LABEL_OVERRIDES = {
  '9': 'L41 Flemington',
  '15': 'L60 Hardeeville',
};

const getLocationKeysFromLtlData = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.keys(raw).filter((key) => Array.isArray(raw[key]));
};

const mergeLtlRecords = (raw, locationKeys) =>
  locationKeys.flatMap((loc) => (Array.isArray(raw[loc]) ? raw[loc] : []));

const FULFILLED_REGEX = /^\s*(\d+)\s*\/\s*\1(\b|$)/i;

const preIndexRecord = (r, idx) => {
  const id = r.id ?? r._id ?? idx;
  const rawShipdate = r.shipdate || r.shipDate || r.ship_date || '';
  let shipTimestamp = 0;
  if (rawShipdate) {
    const t = Date.parse(rawShipdate);
    shipTimestamp = Number.isNaN(t) ? 0 : t;
  }

  const carrierNameStr = String(r.carrierName || '').trim();
  const carrierLower = carrierNameStr.toLowerCase();

  const poNumStr = String(r.poNumber || r.poNumberId || '').trim();
  const poLower = poNumStr.toLowerCase();
  const digitMatch = poNumStr.match(/\d/);
  const poFirstDigit = digitMatch ? parseInt(digitMatch[0], 10) : null;

  const rawStatus = r.status || '';
  const isFulfilled = FULFILLED_REGEX.test(rawStatus);
  const displayStatus = isFulfilled ? 'Ready' : rawStatus || 'No Status';

  // Build lightweight pre-indexed search string across all common fields
  const searchStr = `${poNumStr} ${r.arnValue || ''} ${carrierNameStr} ${rawStatus} ${displayStatus} ${r.palletCount || ''} ${r.palletSize || ''} ${rawShipdate}`.toLowerCase();

  return {
    ...r,
    id,
    _shipTimestamp: shipTimestamp,
    _carrierLower: carrierLower,
    _poLower: poLower,
    _poFirstDigit: poFirstDigit,
    _displayStatus: displayStatus,
    _searchStr: searchStr,
  };
};

const normalizeLtlRecords = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map(preIndexRecord);
  }
  if (raw && typeof raw === 'object') {
    const keys = getLocationKeysFromLtlData(raw);
    return mergeLtlRecords(raw, keys).map(preIndexRecord);
  }
  return [];
};

const parseLocationsList = (locationsData) => {
  if (!locationsData) return [];

  const raw = locationsData;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (raw?.data && typeof raw.data === 'object') return Object.values(raw.data);
  if (Array.isArray(raw?.results)) return raw.results;
  if (Array.isArray(raw?.records)) return raw.records;
  if (typeof raw === 'object') return Object.values(raw);
  return [];
};

const buildLocationLabel = (loc) => {
  const id = loc?.id ?? loc?.location_id;
  if (id != null && LOCATION_LABEL_OVERRIDES[String(id)]) {
    return LOCATION_LABEL_OVERRIDES[String(id)];
  }
  return loc?.location || loc?.name || (id != null ? String(id) : '');
};

/** 43" TV / large wall display — 1080p and up */
const LTL_TV_DISPLAY_QUERY = '(min-width: 1920px) and (min-height: 900px)';

const allowedColumns = ['poNumber', 'poNumberId', 'arnValue', 'imageUrl', 'palletCount', 'palletSize', 'shipdate', 'status'];
const viewDetailsColumns = [
  'line_id',
  'item',
  'quantity',
  'location',
  'ready_for_picking',
  'picked',
  'ship_confirmed',
  'tracking_number',
  'sscc_codes',
  'pallet_number',
  'packing_slip_id',
  'bin_number',
  'picked_time',
  'picker_name',
  'ship_confirm_time',
  'movement'
];

const LtlDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const { data: locationsData, isLoading: locationsLoading } = useCachedData(endpoints.locations);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({});
  const [lineLevelDialogOpen, setLineLevelDialogOpen] = useState(false);
  const [lineLevelLoading, setLineLevelLoading] = useState(false);
  const [lineLevelError, setLineLevelError] = useState('');
  const [lineLevelData, setLineLevelData] = useState([]);
  const [lineLevelColumns, setLineLevelColumns] = useState([]);
  const [selectedTransactionId, setSelectedTransactionId] = useState('');
  const { denseMode } = useGlobalDenseMode();
  const isTvDisplay = useMediaQuery(LTL_TV_DISPLAY_QUERY);

  const tvCellFontSize = isTvDisplay ? '1.35rem' : '0.8125rem';
  const tvHeaderFontSize = isTvDisplay ? '1.2rem' : undefined;
  const tvRowHeight = isTvDisplay ? 72 : undefined;
  const tvColumnHeaderHeight = isTvDisplay ? 64 : undefined;

  const locationOptions = useMemo(() => {
    const options = parseLocationsList(locationsData)
      .map((loc) => {
        const value = String(loc?.location ?? loc?.name ?? loc?.id ?? loc?.location_id ?? '');
        const label = buildLocationLabel(loc);
        if (!value || !label) return null;
        return { value, label };
      })
      .filter(Boolean);

    return [...new Map(options.map((option) => [option.value, option])).values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [locationsData]);

  const apiLocationParam = useMemo(() => {
    if (!selectedLocation || selectedLocation === 'all') return null;
    return selectedLocation;
  }, [selectedLocation]);

  const {
    data: ltlRawData,
    isLoading,
    error: ltlError,
    refetch,
  } = useQuery({
    queryKey: ['wms_data', 'ltlRecords', apiLocationParam ?? 'all'],
    queryFn: async () => {
      const response = await api.get(endpoints.ltlRecords, {
        params: apiLocationParam ? { location: apiLocationParam } : undefined,
      });
      return response.data?.data ?? response.data;
    },
    enabled: selectedLocation === 'all' || Boolean(apiLocationParam),
    refetchInterval: 30000,
    staleTime: 20000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    retry: 1,
  });

  const records = useMemo(() => normalizeLtlRecords(ltlRawData), [ltlRawData]);
  const fetchError = ltlError?.message || (ltlError ? 'Failed to load records' : null);

  // Initialize/update visible columns based on allowed columns only
  useEffect(() => {
    if (Array.isArray(records) && records.length > 0) {
      const keySet = new Set();
      records.forEach((r) => Object.keys(r || {}).forEach((k) => keySet.add(k)));
      const keys = Array.from(keySet).filter((key) => allowedColumns.includes(key));
      setVisibleColumns((prev) => {
        const next = { ...prev };
        keys.forEach((k) => {
          if (typeof next[k] === 'undefined') next[k] = true;
        });
        return next;
      });
    }
  }, [records]);

  // Derive columns from response keys but only include allowed columns in specific order
  const allKeys = useMemo(() => {
    if (!Array.isArray(records) || records.length === 0) return [];
    const keySet = new Set();
    records.forEach((r) => Object.keys(r || {}).forEach((k) => keySet.add(k)));
    return allowedColumns.filter((key) => keySet.has(key));
  }, [records]);

  const visibleKeys = useMemo(() => allKeys.filter((k) => visibleColumns[k] !== false), [allKeys, visibleColumns]);

  // Fast filtered & sorted data using pre-indexed fields (0 regex/Date allocations during sort)
  const filteredData = useMemo(() => {
    let data = records;
    if (deferredSearchTerm) {
      const term = deferredSearchTerm.toLowerCase().trim();
      data = records.filter((item) =>
        item._searchStr ? item._searchStr.includes(term) : JSON.stringify(item).toLowerCase().includes(term)
      );
    }

    // Sort by shipdate (Old to New), then carrierName alphabetically, then PO number numerically
    return [...data].sort((a, b) => {
      // 1) Prioritize shipdate (earliest first, records without dates at the end)
      const aShip = a._shipTimestamp || 0;
      const bShip = b._shipTimestamp || 0;
      if (aShip !== bShip) {
        if (!aShip) return 1;
        if (!bShip) return -1;
        return aShip - bShip;
      }

      // 2) Second priority: sort by carrierName
      const aCarrier = a._carrierLower || '';
      const bCarrier = b._carrierLower || '';
      if (aCarrier !== bCarrier) {
        if (!aCarrier) return 1;
        if (!bCarrier) return -1;
        return aCarrier.localeCompare(bCarrier);
      }

      // 3) Third priority: sort by PO number
      const aFirst = a._poFirstDigit;
      const bFirst = b._poFirstDigit;
      if (aFirst != null && bFirst != null && aFirst !== bFirst) {
        return aFirst - bFirst;
      }
      if (aFirst != null && bFirst == null) return -1;
      if (aFirst == null && bFirst != null) return 1;

      const aPo = a._poLower || '';
      const bPo = b._poLower || '';
      return aPo.localeCompare(bPo);
    });
  }, [deferredSearchTerm, records]);

  const handleColumnMenuOpen = useCallback((e) => setColumnMenuAnchorEl(e.currentTarget), []);
  const handleColumnMenuClose = useCallback(() => setColumnMenuAnchorEl(null), []);
  const toggleColumn = useCallback((key) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] })), []);

  const getTransactionId = (row) => {
    const value = row?.transaction_id || row?.transactionId || row?.tracking_number || row?.trackingNumber || '';
    return String(value).trim();
  };

  const handleRowClick = useCallback(async (row) => {
    const transactionId = getTransactionId(row);
    setSelectedTransactionId(transactionId);
    setLineLevelDialogOpen(true);
    setLineLevelLoading(true);
    setLineLevelError('');
    setLineLevelData([]);
    setLineLevelColumns([]);

    if (!transactionId) {
      setLineLevelError('Transaction ID not found for this row.');
      setLineLevelLoading(false);
      return;
    }

    try {
      const response = await api.get(endpoints.netsuiteSoLineLevel, {
        params: { transaction_id: transactionId },
      });
      const payload = response?.data?.data ?? response?.data;
      const rows = enrichLtlLineLevelRows(Array.isArray(payload) ? payload : (payload ? [payload] : []));

      setLineLevelData(rows);
      const sourceKeys = Object.keys(rows[0] || {});
      const orderedLineLevelColumns = viewDetailsColumns.filter((key) => sourceKeys.includes(key));
      setLineLevelColumns(orderedLineLevelColumns.length ? orderedLineLevelColumns : sourceKeys);
    } catch (err) {
      setLineLevelError(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load line-level data.'
      );
    } finally {
      setLineLevelLoading(false);
    }
  }, []);

  const ltlGridColumns = useMemo(
    () =>
      visibleKeys.map((key) => ({
        accessorKey: key,
        header: labelForLtlColumn(key),
        minSize: key === 'imageUrl' ? (isTvDisplay ? 160 : 100) : isTvDisplay ? 180 : 130,
        size: key === 'imageUrl' ? (isTvDisplay ? 220 : 160) : key === 'status' ? (isTvDisplay ? 260 : 200) : isTvDisplay ? 240 : 180,
        cell: (info) => {
          const row = info.row.original;
          if (key === 'status') {
            return (
              <LtlStatusCell
                displayStatus={row._displayStatus}
                status={row[key]}
                isTvDisplay={isTvDisplay}
              />
            );
          }
          if (key === 'imageUrl') {
            return (
              <CarrierLogoCell
                imageUrl={row[key]}
                isTvDisplay={isTvDisplay}
                denseMode={denseMode}
              />
            );
          }
          return (
            <LtlTextCell
              value={row[key]}
              fontSize={tvCellFontSize}
              isTvDisplay={isTvDisplay}
            />
          );
        },
      })),
    [visibleKeys, denseMode, isTvDisplay, tvCellFontSize]
  );

  const ltlTvGridSx = useMemo(
    () => ({
      '& .MuiDataGrid-main, & .MuiDataGrid-virtualScroller, & .MuiDataGrid-virtualScrollerContent, & .MuiDataGrid-virtualScrollerRenderZone, & .MuiDataGrid-row, & .MuiDataGrid-cell, & .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
        transition: 'none !important',
        animation: 'none !important',
      },
      '& .MuiDataGrid-virtualScroller': {
        willChange: 'scroll-position',
        WebkitOverflowScrolling: 'touch',
      },
      '& .MuiDataGrid-row': {
        contain: 'layout style',
      },
      '& .MuiDataGrid-row:hover, & .MuiDataGrid-cell:hover, & .MuiDataGrid-columnHeader:hover': {
        transition: 'none !important',
        transform: 'none !important',
      },
      '& .MuiChip-root': {
        transition: 'none !important',
        animation: 'none !important',
        transform: 'none !important',
      },
      '& img': {
        transition: 'none !important',
        animation: 'none !important',
        transform: 'none !important',
      },
      '& .MuiCircularProgress-root': {
        animation: 'circular-rotate 1.4s linear infinite !important',
      },
      '& .MuiCircularProgress-svg': {
        display: 'block',
      },
      ...(isTvDisplay
        ? {
            '& .MuiDataGrid-columnHeaderTitle': { fontSize: tvHeaderFontSize, fontWeight: 700 },
            '& .MuiDataGrid-cell': { fontSize: tvCellFontSize },
          }
        : {}),
    }),
    [isTvDisplay, tvCellFontSize, tvHeaderFontSize]
  );

  const actionButtons = useMemo(
    () => [
      <React.Fragment key="ltl-col-menu">
        <Tooltip title="Show/Hide Columns">
          <IconButton onClick={handleColumnMenuOpen} size="small" aria-label="Show/Hide Columns" sx={{ p: '6px' }}>
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={columnMenuAnchorEl}
          open={Boolean(columnMenuAnchorEl)}
          onClose={handleColumnMenuClose}
          transitionDuration={0}
          TransitionProps={{ timeout: 0 }}
          PaperProps={{
            sx: {
              maxHeight: '70vh',
              minWidth: 220,
              p: 1,
              transition: 'none !important',
              animation: 'none !important',
              transform: 'none !important',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Show/Hide Columns
            </Typography>
            <IconButton size="small" onClick={handleColumnMenuClose} sx={{ p: 0.5 }} aria-label="Close column visibility menu">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {allKeys.map((k) => (
            <MenuItem key={k} onClick={() => toggleColumn(k)} sx={{ fontSize: '0.875rem', minHeight: 'auto', py: 0.75, px: 1 }}>
              <Checkbox checked={visibleColumns[k] !== false} size="small" sx={{ p: 0.5, mr: 1 }} />
              <ListItemText primary={labelForLtlColumn(k)} primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.2 } }} />
            </MenuItem>
          ))}
        </Menu>
      </React.Fragment>,
    ],
    [allKeys, visibleColumns, columnMenuAnchorEl, handleColumnMenuOpen, handleColumnMenuClose, toggleColumn]
  );

  const ltlMainGrid = useDataGridProTable({
    data: filteredData,
    loading: isLoading,
    error: fetchError,
    title: 'LTL Records',
    columns: ltlGridColumns,
    onDataRefresh: refetch,
    onRowClick: handleRowClick,
    getRowId: (row) => String(row?.id ?? row?._id ?? ''),
    hideHeader: true,
    hideQuickFilter: true,
    disablePaper: true,
    disablePagination: isTvDisplay,
    rowHeight: tvRowHeight,
    columnHeaderHeight: tvColumnHeaderHeight,
    gridSx: ltlTvGridSx,
    muiTableContainerProps: { sx: { flex: 1, minHeight: 0 } },
    initialSorting: [{ id: 'shipdate', desc: false }],
    actionButtons,
  });

  const lineLevelGridColumns = useMemo(
    () =>
      lineLevelColumns.map((col) => {
        const width = LINE_LEVEL_COLUMN_WIDTHS[col] ?? 150;
        return {
          accessorKey: col,
          header: labelForLineLevelColumn(col),
          flex: 0,
          minWidth: width,
          width,
          cell: (info) => {
            const v = info.getValue();
            if (v === null || v === undefined || v === '') return '-';
            const text = typeof v === 'object' ? JSON.stringify(v) : String(v);
            if (col === 'pallet_number') {
              return (
                <Tooltip title={text} placement="top-start">
                  <Typography variant="body2" noWrap sx={{ width: '100%' }}>
                    {text}
                  </Typography>
                </Tooltip>
              );
            }
            return text;
          },
        };
      }),
    [lineLevelColumns]
  );

  const lineLevelDetailGrid = useDataGridProTable({
    data: lineLevelData,
    loading: lineLevelLoading,
    error: lineLevelError || null,
    title: 'Line-level details',
    columns: lineLevelGridColumns,
    hideHeader: true,
    hideQuickFilter: true,
    disablePaper: true,
    muiTableContainerProps: { sx: { height: 'min(58vh, 520px)', minWidth: 0 } },
    gridSx: {
      width: '100%',
      minWidth: 0,
      '& .MuiDataGrid-main': {
        overflow: 'hidden',
        transition: 'none !important',
      },
      '& .MuiDataGrid-virtualScroller': {
        overflowX: 'auto !important',
        overflowY: 'auto !important',
        overscrollBehavior: 'contain',
        transition: 'none !important',
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        minWidth: 'max-content !important',
        transition: 'none !important',
      },
      '& .MuiDataGrid-scrollbar--horizontal': {
        display: 'block',
      },
      '& .MuiDataGrid-row, & .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
        transition: 'none !important',
        animation: 'none !important',
      },
      '& .MuiDataGrid-row:hover, & .MuiDataGrid-cell:hover': {
        transition: 'none !important',
        transform: 'none !important',
      },
      '& .MuiCircularProgress-root': {
        animation: 'circular-rotate 1.4s linear infinite !important',
      },
      '& .MuiCircularProgress-svg': {
        display: 'block',
      },
    },
    getRowId: (row, index) =>
      String(row?.line_id ?? row?.internal_id ?? row?.id ?? (index != null ? `line-${index}` : 'line')),
  });

  return (
    <DashboardLayout>
      <Box
        sx={{
          p: isTvDisplay ? 2.5 : 1.5,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Typography
          variant={isTvDisplay ? 'h3' : 'h5'}
          component="h1"
          sx={{
            mb: isTvDisplay ? 2.5 : 1.5,
            fontWeight: 'bold',
            color: 'primary.main',
            fontSize: isTvDisplay ? '2.5rem' : '1.25rem',
          }}
        >
          LTL Dashboard
        </Typography>

        <Box
          sx={{
            mb: isTvDisplay ? 2.5 : 1.5,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: isTvDisplay ? 2 : 1,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex' }}>
            <TextField
              fullWidth
              placeholder="Search by PO number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size={isTvDisplay ? 'medium' : 'small'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: isTvDisplay ? '2rem' : '1.25rem' }} />
                  </InputAdornment>
                ),
                sx: { fontSize: isTvDisplay ? '1.35rem' : '0.875rem', minHeight: isTvDisplay ? 56 : undefined },
              }}
              inputProps={{ sx: { py: isTvDisplay ? 1.5 : undefined } }}
            />
          </Box>
          <Box sx={{ minWidth: isTvDisplay ? 320 : { xs: 140, sm: 160 } }}>
            <TextField
              select
              fullWidth
              label="Location"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              size={isTvDisplay ? 'medium' : 'small'}
              disabled={locationsLoading}
              InputLabelProps={{ sx: { fontSize: isTvDisplay ? '1.2rem' : '0.875rem' } }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: isTvDisplay ? '1.35rem' : '0.875rem',
                  height: isTvDisplay ? 56 : '36px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: isTvDisplay ? '1.2rem' : '0.875rem',
                },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: isTvDisplay ? '1.25rem' : '0.875rem' }}>
                All Locations
              </MenuItem>
              {locationsLoading ? (
                <MenuItem disabled sx={{ fontSize: isTvDisplay ? '1.25rem' : '0.875rem' }}>
                  Loading locations...
                </MenuItem>
              ) : (
                locationOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ fontSize: isTvDisplay ? '1.25rem' : '0.875rem' }}
                  >
                    {option.label}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Box>
        </Box>

        <Paper elevation={2} sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              py: isTvDisplay ? 1.5 : 0.75,
              px: isTvDisplay ? 2.5 : 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{ fontSize: isTvDisplay ? '1.75rem' : '1.125rem', fontWeight: 700 }}
            >
              LTL Records
            </Typography>
          </Box>
          <Box sx={{ px: 0.5, pb: 0.5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <MuiXDataGridSurface hook={ltlMainGrid} DataGridPro={DataGridPro} />
          </Box>
        </Paper>

        <Dialog
          open={lineLevelDialogOpen}
          onClose={() => setLineLevelDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          transitionDuration={0}
          TransitionProps={{ timeout: 0 }}
          sx={{
            '& .MuiDialog-paper': {
              transition: 'none !important',
              animation: 'none !important',
              transform: 'none !important',
            },
            '& .MuiBackdrop-root': {
              transition: 'none !important',
              animation: 'none !important',
            },
            '& .MuiCircularProgress-root': {
              animation: 'circular-rotate 1.4s linear infinite !important',
            },
            '& .MuiCircularProgress-svg': {
              display: 'block',
            },
          }}
        >
          <DialogTitle sx={{ pr: 6 }}>
            Line-Level Details {selectedTransactionId ? `- ${selectedTransactionId}` : ''}
            <IconButton
              aria-label="close"
              onClick={() => setLineLevelDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ overflow: 'hidden' }}>
            {lineLevelError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {lineLevelError}
              </Alert>
            ) : null}

            {lineLevelLoading ? (
              <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : null}

            {!lineLevelLoading && !lineLevelError && lineLevelData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No line-level records found.
              </Typography>
            ) : null}

            {!lineLevelLoading && lineLevelData.length > 0 ? (
              <Box sx={{ width: '100%', mt: 0.5, minWidth: 0, overflow: 'hidden' }}>
                <MuiXDataGridSurface hook={lineLevelDetailGrid} DataGridPro={DataGridPro} />
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default LtlDashboard;
